import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, FileText, Image as ImageIcon, Trash2, Eye, EyeOff, Download, FolderOpen } from "lucide-react";
import { formatDate } from "@/lib/format";

const CATEGORIES = [
  { v: "contract", l: "Contract" },
  { v: "rate_card", l: "Rate card" },
  { v: "invoice", l: "Invoice" },
  { v: "insurance", l: "Insurance" },
  { v: "other", l: "Other" },
];
const CAT_LABEL = (v) => CATEGORIES.find((c) => c.v === v)?.l || "Other";
const isImage = (ct) => (ct || "").startsWith("image/");

export default function DocumentsTab({ accountId, onChanged }) {
  const fileRef = useRef(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("contract");
  const [shareNew, setShareNew] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    api.get(`/admin/accounts/${accountId}/documents`)
      .then(({ data }) => setDocs(data.documents || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    let uploaded = 0;
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) { toast.error(`${file.name} is over 25 MB`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", file.name.replace(/\.[^.]+$/, ""));
        fd.append("category", category);
        fd.append("shared_with_customer", shareNew ? "true" : "false");
        await api.post(`/admin/accounts/${accountId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded += 1;
      }
      if (uploaded > 0) { toast.success(`Uploaded ${uploaded} file${uploaded === 1 ? "" : "s"}`); load(); onChanged?.(); }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleShare = async (d) => {
    try {
      await api.patch(`/admin/documents/${d.id}`, { shared_with_customer: !d.shared_with_customer });
      setDocs((arr) => arr.map((x) => (x.id === d.id ? { ...x, shared_with_customer: !x.shared_with_customer } : x)));
      toast.success(!d.shared_with_customer ? "Shared with customer" : "Hidden from customer");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    try {
      await api.delete(`/admin/documents/${d.id}`);
      setDocs((arr) => arr.filter((x) => x.id !== d.id));
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="documents-tab">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-slate-500" />
          <h3 className="text-base font-bold text-slate-900">Documents</h3>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="doc-category" className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 pb-2.5 cursor-pointer" data-testid="doc-share-new-label">
            <input type="checkbox" checked={shareNew} onChange={(e) => setShareNew(e.target.checked)} data-testid="doc-share-new" /> Share on upload
          </label>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-upload-btn"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 rounded-md">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
          </button>
          <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.docx,.xlsx,.csv,.txt" onChange={(e) => onFiles(e.target.files)} className="hidden" data-testid="doc-file-input" />
        </div>
      </div>

      <p className="text-xs text-slate-400">Uploading a file with the same name creates a new version. Toggle the eye to share a document to the customer's Documents page.</p>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : error ? (
        <div className="py-8 text-center"><p className="text-sm font-semibold text-red-600">Couldn't load documents.</p><button onClick={load} className="mt-2 text-sm font-semibold text-blue-600">Retry</button></div>
      ) : docs.length === 0 ? (
        <p className="text-sm text-slate-400 py-8 text-center">No documents yet — upload contracts, rate cards or insurance certificates.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="documents-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2.5 px-3 font-medium">Name</th>
                <th className="py-2.5 px-3 font-medium">Category</th>
                <th className="py-2.5 px-3 font-medium">Version</th>
                <th className="py-2.5 px-3 font-medium">Uploaded</th>
                <th className="py-2.5 px-3 font-medium">Shared</th>
                <th className="py-2.5 px-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-b border-slate-100">
                  <td className="py-2.5 px-3">
                    <span className="inline-flex items-center gap-2 font-medium text-slate-800">
                      {isImage(d.content_type) ? <ImageIcon className="w-4 h-4 text-blue-500" /> : <FileText className="w-4 h-4 text-slate-400" />}
                      {d.name}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-slate-500">{CAT_LABEL(d.category)}</td>
                  <td className="py-2.5 px-3 text-slate-500">v{d.version}</td>
                  <td className="py-2.5 px-3 text-slate-500 whitespace-nowrap">{formatDate(d.created_at)}</td>
                  <td className="py-2.5 px-3">
                    <button onClick={() => toggleShare(d)} data-testid={`doc-share-${d.id}`}
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${d.shared_with_customer ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {d.shared_with_customer ? <><Eye className="w-3.5 h-3.5" /> Shared</> : <><EyeOff className="w-3.5 h-3.5" /> Internal</>}
                    </button>
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <div className="inline-flex items-center gap-3 justify-end">
                      <a href={`${API_BASE}/documents/files/${d.storage_path}`} target="_blank" rel="noreferrer" data-testid={`doc-download-${d.id}`} className="text-slate-500 hover:text-blue-600"><Download className="w-4 h-4" /></a>
                      <button onClick={() => remove(d)} data-testid={`doc-delete-${d.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
